/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormBuilder, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Claim } from '@wsphere/warranties/domain';
import { map, tap } from 'rxjs';
import { currentEligibilityInputs } from '../../components/claim-item-eligibility/claim-item-eligibility.component';
import { CLAIM_ITEM_CONTROLS_CONFIG } from '../../config/claim-item-controls.config';
import { CLAIM_ITEM_DECISION_CONFIG } from '../../config/claim-item-decision.config';
import { ClaimItemHoldService, ClaimItemLineService, ClaimService } from '../../services';

type DecisionAction = 'approve' | 'reject' | 'redraft';

function toCents(dollars: number) {
  return Math.round(dollars * 100);
}

// Dollars here, checked in cents so float noise can't push an exact amount over.
function authorizedAmountValidator(requestedAmount: number): ValidatorFn {
  return control => {
    const value = control.value;
    if (value === null || value === undefined || value === '') return { required: true };

    const dollars = Number(value);
    if (!Number.isFinite(dollars) || dollars < 0) return { min: true };
    if (Math.abs(dollars * 100 - toCents(dollars)) > 1e-6) return { cents: true };

    return toCents(dollars) > requestedAmount ? { max: true } : null;
  };
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-adjudicate-dialog',
  templateUrl: './claim-item-adjudicate.dialog.html',
  standalone: false,
})
export class ClaimItemAdjudicateDialog {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private claimSvc: ClaimService,
    private lineSvc: ClaimItemLineService,
    private formBuilder: FormBuilder,
    private holdSvc: ClaimItemHoldService
  ) {}

  readonly config = CLAIM_ITEM_DECISION_CONFIG;
  readonly controlsConfig = CLAIM_ITEM_CONTROLS_CONFIG;

  readonly claim$ = this.claimSvc.getClaim().pipe(tap(claim => (this.claim = claim)));

  readonly item$ = this.route.data.pipe(
    map(data => data['item'] as Claim.ClaimItem.Model),
    tap(item => {
      if (item) {
        this.item = item;
        this.form.patchValue({
          status: item.status === Claim.ClaimItem.Status.RESOLVED ? Claim.ClaimItem.Status.APPROVED : item.status,
          reason: item.adjudicationReason,
        });

        void this.load(item);
      }
    })
  );

  form = this.formBuilder.group({
    status: [Claim.ClaimItem.Status.DRAFT as Claim.ClaimItem.Status, Validators.required],
    reason: ['', Validators.required],
  });

  // Used only when the job has charges; `detail` is the approve/reject details or the reviewing note.
  decisionForm = this.formBuilder.group({
    approveReason: [null as string | null],
    rejectReason: [null as string | null],
    detail: [''],
    amounts: this.formBuilder.array<FormControl<number | null>>([]),
  });

  /** The job's active charges; null until loaded. Jobs without charges keep the free-text form. */
  charges: Claim.ClaimItem.Line.Model[] | null = null;
  reasons: Claim.ClaimItem.Decision.Reasons = { approve: [], reject: [] };
  loadFailed = false;
  saving = false;
  /** Resolved jobs with charges keep their decision; the server rejects changes to them. */
  resolved = false;
  /** Open evidence holds block approval; the server checks too. */
  openHolds = 0;

  claim: Claim.Model | null = null;
  item: Claim.ClaimItem.Model | null = null;

  /** The latest eligibility check; undefined until loaded. Only used when the policy checks eligibility. */
  eligibility: Claim.ClaimItem.Eligibility.Model | null | undefined = undefined;

  reviewing = Claim.ClaimItem.Status.DRAFT;
  approved = Claim.ClaimItem.Status.APPROVED;
  rejected = Claim.ClaimItem.Status.REJECTED;

  get amounts() {
    return this.decisionForm.controls.amounts;
  }

  async load(item: Claim.ClaimItem.Model) {
    this.charges = null;
    this.eligibility = undefined;
    this.loadFailed = false;
    this.resolved = item.status === Claim.ClaimItem.Status.RESOLVED;

    const [lines, reasons, holds] = await Promise.all([
      this.lineSvc.list(item.claimId, item.id).catch(() => null),
      this.claimSvc.getDecisionReasons().catch(() => null),
      this.holdSvc.holds(item.claimId, item.id).catch(() => null),
    ]);

    this.openHolds = (holds?.items ?? []).filter(hold => hold.status === Claim.ClaimItem.Hold.Status.OPEN).length;

    const charges = lines?.items.filter(line => !line.deleted) ?? null;

    // Without the reasons a job with charges can't be decided, so don't offer a form that can't save.
    if (!charges || (charges.length && !reasons)) {
      this.loadFailed = true;
      return;
    }

    this.reasons = reasons ?? { approve: [], reject: [] };
    this.decisionForm.setControl(
      'amounts',
      this.formBuilder.array(
        charges.map(line => {
          // Can't approve more than was requested, so cap the recommendation there.
          const recommended = Math.min(line.allowedAmount ?? line.requestedAmount, line.requestedAmount);
          return new FormControl<number | null>(
            // A rejection's $0s aren't a real price, so start from the recommendation.
            (item.status === Claim.ClaimItem.Status.REJECTED ? recommended : (line.authorizedAmount ?? recommended)) /
              100,
            authorizedAmountValidator(line.requestedAmount)
          );
        })
      )
    );
    this.charges = charges;
  }

  changeStatus(status: Claim.ClaimItem.Status) {
    this.form.patchValue({ status });
  }

  isIncluded(claim: Claim.Model, item: Claim.ClaimItem.Model) {
    return !!item.coverageId && Claim.ClaimItem.included(claim, item.coverageId);
  }

  statusTheme(status: Claim.ClaimItem.Status) {
    return Claim.ClaimItem.getTheme(status);
  }

  requestedTotal() {
    return (this.charges ?? []).reduce((sum, line) => sum + line.requestedAmount, 0);
  }

  currentAuthorizedTotal() {
    return (this.charges ?? []).reduce((sum, line) => sum + (line.authorizedAmount ?? 0), 0);
  }

  /** What the adjuster has entered so far; blank or invalid amounts count as $0. */
  authorizedTotal() {
    return this.amounts.controls.reduce(
      (sum, control) => sum + (control.valid && control.value !== null ? toCents(control.value) : 0),
      0
    );
  }

  /** Advice only: going above the recommendation is allowed. */
  aboveAllowed(line: Claim.ClaimItem.Line.Model, index: number) {
    const control = this.amounts.at(index);
    const allowed = line.allowedAmount ?? null;
    return allowed !== null && !!control?.valid && toCents(control.value ?? 0) > allowed;
  }

  formatAmount(amount: number | null | undefined) {
    if (amount === null || amount === undefined) return '-';
    return '$' + (amount / 100).toFixed(2);
  }

  decisionValid() {
    const status = this.form.value.status;
    const value = this.decisionForm.value;

    if (status === this.approved) return !!value.approveReason && this.amounts.valid && !this.openHolds;
    if (status === this.rejected) return !!value.rejectReason;
    return status === this.reviewing;
  }

  readonly usesNewFlow = Claim.Authorization.usesNewFlow;

  /** Eligibility is part of the new claims flow. */
  get checksEligibility() {
    return !!this.claim && this.usesNewFlow(this.claim) && !!this.claim.warranty?.policy?.checkClaimEligibility;
  }

  /** A new-flow job without charges has nothing to authorize, so it can't be approved; rejecting still works. */
  needsChargesToApprove() {
    return (
      this.form.value.status === this.approved &&
      !!this.claim &&
      this.usesNewFlow(this.claim) &&
      this.charges?.length === 0
    );
  }

  /** Whether the latest eligibility check stops this approval; the server enforces the same rule. */
  eligibilityBlocksApproval() {
    if (!this.checksEligibility || this.form.value.status !== this.approved) return false;
    if (this.eligibility === undefined) return true;

    const authorized = this.charges?.length ? this.authorizedTotal() : null;
    const current = currentEligibilityInputs(this.claim, this.item);
    return !!Claim.ClaimItem.Eligibility.approvalProblem(this.eligibility, current, authorized);
  }

  saveDisabled() {
    return !!this.saveBlockedReason();
  }

  /** Why Save is off, shown on hover; null when it can save. */
  saveBlockedReason(): { title: string; subtitle: string } | null {
    const blocked = this.config.saveBlocked;

    if (!this.charges) {
      return this.loadFailed
        ? { title: blocked.loadFailed, subtitle: this.config.form.loadFailed }
        : { title: blocked.loading, subtitle: this.config.form.loading };
    }
    // New-flow jobs are approved on their charges; the server checks too.
    if (this.needsChargesToApprove()) return blocked.charges;
    if (this.eligibilityBlocksApproval()) {
      return this.eligibility === undefined ? blocked.checkingEligibility : blocked.notEligible;
    }

    const status = this.form.value.status;
    const holds = { title: blocked.openHolds, subtitle: this.controlsConfig.adjudicate.openHolds };

    // Jobs without charges keep the free-text form.
    if (!this.charges.length) {
      if (status === this.approved && this.openHolds) return holds;
      return status !== this.reviewing && !this.form.value.reason ? blocked.reasoning : null;
    }

    if (this.saving) return blocked.saving;
    if (this.resolved) return { title: blocked.resolved, subtitle: this.config.form.resolved };

    const value = this.decisionForm.value;
    if (status === this.approved) {
      if (!value.approveReason) return blocked.reasonPick;
      if (!this.amounts.valid) return { title: blocked.amounts, subtitle: this.config.form.amountHint };
      if (this.openHolds) return holds;
    }
    if (status === this.rejected && !value.rejectReason) return blocked.reasonPick;

    // Same rules as decisionValid(); anything it still rejects falls back to asking for a reason.
    return this.decisionValid() ? null : blocked.reasonPick;
  }

  async save(item: Claim.ClaimItem.Model) {
    if (!this.charges) return;
    if (this.charges.length) {
      await this.saveDecision(item, this.charges);
      return;
    }

    const value = this.form.value;

    if (value.status === Claim.ClaimItem.Status.APPROVED) {
      if (!value.reason) return;

      const itemId = await this.claimSvc.approveItem(item.claimId, item.id, value.reason).catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });

        this.navigateTo();
      });

      if (itemId) {
        this.toastSvc.show({
          type: 'success',
          title: (item.coverage?.title ?? 'Other') + ' Item Approved',
          text: 'The ' + (item.coverage?.title ?? 'Other') + ' item has been successfully adjudicated as approved.',
        });

        this.claimSvc.refreshClaimTotals();
        await this.claimSvc.refreshClaim();
        this.navigateTo();
      }
    } else if (value.status === Claim.ClaimItem.Status.REJECTED) {
      if (!value.reason) return;

      const itemId = await this.claimSvc.rejectItem(item.claimId, item.id, value.reason).catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });

        this.navigateTo();
      });

      if (itemId) {
        this.toastSvc.show({
          type: 'success',
          title: (item.coverage?.title ?? 'Other') + ' Item Rejected',
          text: 'The ' + (item.coverage?.title ?? 'Other') + ' item has been successfully adjudicated as rejected.',
        });

        this.claimSvc.refreshClaimTotals();
        await this.claimSvc.refreshClaim();
        this.navigateTo();
      }
    } else if (value.status === Claim.ClaimItem.Status.DRAFT) {
      const itemId = await this.claimSvc.redraftItem(item.claimId, item.id, value.reason).catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });

        this.navigateTo();
      });

      if (itemId) {
        this.toastSvc.show({
          type: 'success',
          title: (item.coverage?.title ?? 'Other') + ' Item Redrafted',
          text: 'The ' + (item.coverage?.title ?? 'Other') + ' item has been successfully set as draft.',
        });

        this.claimSvc.refreshClaimTotals();
        await this.claimSvc.refreshClaim();
        this.navigateTo();
      }
    }

    this.navigateTo();
  }

  private async saveDecision(item: Claim.ClaimItem.Model, charges: Claim.ClaimItem.Line.Model[]) {
    if (this.saving || !this.decisionValid()) return;

    const status = this.form.value.status;
    const value = this.decisionForm.value;
    const detail = value.detail?.trim() || null;
    const expectedVersion = item.decisionVersion ?? 0;
    const coverage = item.coverage?.title ?? this.config.otherCoverage;

    let action: DecisionAction;
    let body:
      | Claim.ClaimItem.ApproveClaimItemRequest
      | Claim.ClaimItem.RejectClaimItemRequest
      | Claim.ClaimItem.RedraftClaimItemRequest;
    let toast: { title: string; text: string };

    if (status === this.approved) {
      action = 'approve';
      body = {
        reasonCode: value.approveReason ?? undefined,
        detail,
        expectedVersion,
        lines: charges.map((line, index) => ({
          lineId: line.id,
          authorizedAmount: toCents(this.amounts.at(index).value ?? 0),
        })),
      };
      toast = this.config.toasts.approved(coverage);
    } else if (status === this.rejected) {
      action = 'reject';
      body = { reasonCode: value.rejectReason ?? undefined, detail, expectedVersion };
      toast = this.config.toasts.rejected(coverage);
    } else {
      action = 'redraft';
      body = { reason: detail, expectedVersion };
      toast = this.config.toasts.redrafted(coverage);
    }

    this.saving = true;
    try {
      // On failure stay open so the adjuster keeps what they entered.
      const result = await this.claimSvc
        .decideItem(item.claimId, item.id, action, body)
        .catch((response: HttpErrorResponse) => {
          if (response?.status === 409) {
            this.toastSvc.show({ type: 'error', ...this.config.toasts.conflict });
          } else {
            const message = response?.error?.message;
            this.toastSvc.show({
              type: 'error',
              title: this.config.toasts.errorTitle,
              text: Array.isArray(message) ? message.join(' ') : message,
            });
          }

          return null;
        });

      if (!result) return;

      this.toastSvc.show({ type: 'success', ...toast });

      this.claimSvc.refreshClaimTotals();
      await this.claimSvc.refreshClaim();
      this.navigateTo();
    } finally {
      this.saving = false;
    }
  }

  navigateTo(path?: string) {
    this.router.navigate([path ?? `../adjudicate`], { relativeTo: this.route });
  }
}
